import { useRef } from 'react';
import {
   QueryFunctionContext,
   QueryKey,
   useInfiniteQuery,
   UseInfiniteQueryOptions,
   UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { ApiClient } from '@dozuki/web-js/utils';
import { useRouter } from 'next/router';

/**
 * useIncrementQuery
 * @description - Used in place of 'useQuery' (or 'useInfiniteQuery') as a convenience method
 *    for loading additional results from a paginated set when the user requests additional
 *    data
 *
 * @usage -
 *  useIncrementalQuery(key: string, async (page: number) => {
      let { courses: data, hasMore } = await getCourses({
         ...options,
         page,
      });
      return { page, data, hasMore };
   });
 */

export type UseIncrementalQueryOptions<
   Model,
   KeyType extends QueryKey,
   Meta
> = UseInfiniteQueryOptions<
   Incremental<Model, Meta>,
   any,
   Incremental<Model, Meta>,
   Incremental<Model, Meta>,
   KeyType
> & {
   firstPage: number;
};

export type IncrementalQueryFunction<Model, KeyType extends QueryKey, Meta> = (
   page: number,
   ctx: QueryFunctionContext<KeyType>
) => Incremental<Model, Meta> | Promise<Incremental<Model, Meta>>;

export type UseIncrementalQueryResult<Model, Meta> = UseInfiniteQueryResult & {
   pageNumber: number | undefined;
   flattenedData: Model | undefined;
   total: number | undefined;
   meta?: Meta;
};

const meta = new Map<string, any>();

export function useIncrementalQuery<Model extends any[], Meta>(
   // Expressing the full type definition here for better type hinting
   key: [string, ...any],
   queryFunction: IncrementalQueryFunction<Model, [string, ...any], Meta>,
   options: UseIncrementalQueryOptions<Model, [string, ...any], Meta> = { firstPage: 1 }
): UseIncrementalQueryResult<Model, Meta> {
   const { firstPage, ...infiniteQueryOptions } = options;
   const router = useRouter();

   const serializedKey = JSON.stringify(key);

   const total = useRef<undefined | number>(undefined);

   // Wrap the queryFunction so that we can default the page, and inject it a little more intuitively.
   async function runQuery(queryContext: QueryFunctionContext<[string, ...any]>) {
      const page = queryContext.pageParam === undefined ? firstPage : queryContext.pageParam;
      const result = await queryFunction(page, queryContext);
      if (result.total !== undefined) total.current = result.total;
      if (result.meta !== undefined) meta.set(serializedKey, result.meta);
      return result;
   }

   const result = useInfiniteQuery(key, runQuery, {
      ...infiniteQueryOptions,
      keepPreviousData: true,
      getNextPageParam: ({ page, hasMore }) => (hasMore ? page + 1 : undefined),
      onError(error, ...args) {
         if (ApiClient.isInvalidLoginError(error)) {
            router.push('/Login');
         }

         if (options.onError) {
            options.onError(error, ...args);
         }
      },
      useErrorBoundary: ApiClient.shouldRedirectError,
   });

   // Flatten the pages into a single result set for easier processing
   //   by the calling context
   const flattenedData: Model | undefined = result.data?.pages.reduce(
      (flattenedData, page) => [...flattenedData, ...page.data] as Model,
      [] as unknown as Model
   );

   return {
      ...result,
      pageNumber: result.data?.pages.length,
      total: total.current,
      meta: result.isStale && result.isFetching ? undefined : meta.get(serializedKey),
      flattenedData,
   };
}

type Incremental<Model, Meta> = {
   data: Model;
   page: number;
   hasMore: boolean;
   total?: number;
   meta?: Meta;
};
